import { fromJS, Map as IMap, List as IList } from 'immutable';
import { itemHasAccessory, getItemAccessory } from '../productCartHandler/productCartSelectors';
import { EGIFT_DELIVERY_SHAREABLE, PROMO_LINK } from '../constants/constants';
import { feeTypes, getFeeAmountByTypeAndCost } from '../fees/consumerFeeUtils';
import { getPassingPromoDetailsEnabled } from '../app/bootstrap';

export const filterAndMapKeys = (originData, keyMap) => (
    originData.filter((_, key) => keyMap.has(key)).mapKeys(
        originKey => keyMap.get(originKey, originKey)
    )
);


export const paymentKeyMap = fromJS({
    paymentPlusAuthResponse: 'response',
    hmac: 'hmac'
});

export const giftCardKeyMap = fromJS({
    senderName: 'senderName',
    deliveryDate: 'deliveryDate',
    recipientEmail: 'recipientEmail',
    recipientName: 'recipientName',
    faceplateCode: 'faceplateCode',
    personalMessage: 'message',
    quantity: 'quantity',
    cardValue: 'faceValue',
    selfBuy: 'selfBuy',
    UYOPImageURL: 'uyoImageUrl',
    multipleRecipients: 'multipleRecipients'
});

export const shippingRecipientKeyMap = fromJS({
    fullName: 'fullName',
    street1: 'street',
    street2: 'street2',
    city: 'city',
    state: 'state',
    countryCode: 'countryCode',
    postalCode: 'zipCode'
});

export const metaDataKeyMap = fromJS({
    amount: 'amount',
    currencyCode: 'currencyCode',
    brandCode: 'brandCode',
    loyaltyNumber: 'loyaltyNumber',
    senderOptIn: 'notifyOffers',
    promoCode: 'promoCode',
    locale: 'languageCode',
    discountAmount: 'totalDiscountRewardAmount',
    checkoutId: 'checkoutId',
    cartQualifies: 'cartQualifies',
    uyoImageURL: 'uyoImageURL'
});

export const orderResponseKeyMap = fromJS({
    statement: 'statement',
    orderNumber: 'orderNumber',
    billingAddress: 'billingAddress'
});

export const sanitizedPaymentKeyMap = fromJS({
    fullName: 'fullName',
    firstName: 'firstName',
    lastName: 'lastName',
    street1: 'street1',
    street2: 'street2',
    city: 'city',
    state: 'state',
    postalCode: 'postalCode',
    countryCode: 'countryCode',
    phoneNumber: 'phoneNumber',
    paymentMethod: 'paymentMethod',
    CAPI_URI_VERSION: 'CAPI_URI_VERSION',
    purchaserEmail: 'purchaserEmail',
    senderEmail: 'senderEmail',
    purchaseDate: 'purchaseDate',
    currency: 'currency'
});

export const getFormattedName = (name) => {
    if (name) {
        return name.replace(/\s\s+/g, ' ').replace(/,/g, '').trim();
    }
    return name;
};

// Normalize promo recipient name across different response shapes.
const getPromoRecipientName = (recipient) => {
    if (!recipient) return '';
    const fullName = recipient.get('full_name') || recipient.get('recipient_name');
    if (fullName) return fullName;
    const firstName = recipient.get('first_name');
    const lastName = recipient.get('last_name');
    return [firstName, lastName].filter(Boolean).join(' ').trim();
};


const getPromoItemType = reward => (
    reward.get('certType') === 'PLST' || reward.get('deliveryMethod') === 'PLCMT'
        ? 'PHYSICAL'
        : 'DIGITAL'
);


const getPromoAddress = recipient => ({
    fullName: getPromoRecipientName(recipient),
    street: recipient.get('street', ''),
    street2: recipient.get('street2', ''),
    city: recipient.get('city', ''),
    state: recipient.get('state', ''),
    countryCode: recipient.get('country_code', ''),
    zipCode: recipient.get('zip_code', '')
});

// Map promo rewards into dedicated rewardCards payload (flagged flow).
const promoRewardToRewardCard = (reward, brandCode) => {
    const recipient = reward.get('recipient', IMap());
    const recipientEmail = recipient.get('email', '');
    const recipientName = getPromoRecipientName(recipient);
    const deliveryMethod = reward.get('deliveryMethod', '');
    const itemType = getPromoItemType(reward);
    const isPhysical = itemType === 'PHYSICAL';

    const base = IMap({
        senderName: reward.get('fromName') || reward.get('senderName'),
        recipientName,
        recipientEmail,
        deliveryDate: reward.get('deliveryDate', new Date().toISOString()),
        faceplateCode: reward.getIn(['faceplate', 'code']),
        faceplateUrl: reward.getIn(['faceplate', 'imageURL']) || undefined,
        message: reward.get('message', ''),
        quantity: reward.get('quantity', 1),
        faceValue: Number(reward.get('cardValue', 0)),
        brandCode: reward.get('brandCode', brandCode),
        checkedEmail: Boolean(recipientEmail),
        isSharedViaLink: deliveryMethod === PROMO_LINK,
        selfBuy: reward.get('selfBonus', false),
        uyoImageUrl: reward.get('uyoImageUrl'),
        cartItemToken: reward.get('parentCardCartToken') || reward.get('cartItemToken'),
        digitalActivationFeeAmount: 0,
        itemType
    });

    return isPhysical ? base.set('address', fromJS(getPromoAddress(recipient))) : base;
};


export const paymentSerializer = payment => filterAndMapKeys(payment, paymentKeyMap);

export const accessorySerializer = (accessory, currency) => (
    fromJS({
        code: accessory.get('code'),
        cost: accessory.getIn(['costs', currency, 'cost'])
    })
);

export const giftCardItemSerializer = (item, brandCode, fees = IList(), feeType, currencyCode) => {
    
    const faceplate = item.get('faceplate');
    const faceplateUrl = faceplate && faceplate.get('imageURL');
    const updatedItem = item.set('faceplateCode', faceplate.get('code')).delete('faceplate');
    const amount = Number(item.get('cardValue'));

    const giftCardItem = filterAndMapKeys(updatedItem, giftCardKeyMap).asMutable();
    
    
    if (item.get('selfBuy')) {
        const senderName = item.get('yourName');
        const senderEmail = item.get('yourEmail');
        const formattedSenderName = getFormattedName(senderName);
        giftCardItem.set('recipientName', formattedSenderName);
        giftCardItem.set('senderName', formattedSenderName);
        giftCardItem.set('recipientEmail', senderEmail);
    } else {
        const fieldMappings = {
            senderName: getFormattedName,
            recipientName: getFormattedName,
            senderEmail: null,
            quantity: null
        };

        Object.entries(fieldMappings).forEach(([key, formatter]) => {
            const value = item.get(key);
            if (value) {
                giftCardItem.set(key, formatter ? formatter(value) : value);
            }
        });

        if (item.get('eGiftCardShareOption') === EGIFT_DELIVERY_SHAREABLE) {
            giftCardItem.set('isSharedViaLink', true);
            giftCardItem.delete('recipientEmail');
        }
    }

    const feeAmount = getFeeAmountByTypeAndCost(fees.toJS(), feeType, amount);
    switch (feeType) {
        case feeTypes.UYOP: {
            giftCardItem.set('uyopFeeAmount', feeAmount);
            giftCardItem.set(
                'plasticActivationFeeAmount',
                getFeeAmountByTypeAndCost(fees, feeTypes.PLASTIC_ACTIVATION, amount)
            );
            break;
        }
        case feeTypes.PLASTIC_ACTIVATION: {
            giftCardItem.set('plasticActivationFeeAmount', feeAmount);
            break;
        }
        default:
            giftCardItem.set('digitalActivationFeeAmount', feeAmount);
    }

    if (itemHasAccessory(item)) {
        giftCardItem.set('accessory', accessorySerializer(getItemAccessory(item), currencyCode));
    }

    const greetings = !!item?.get('greetings');
    if (greetings) {
        giftCardItem.set('greetings', item.get('greetings'));
    }

    giftCardItem.set('cartItemToken', item.get('id'));
    giftCardItem.set('brandCode', brandCode);
    if (faceplateUrl) {
        giftCardItem.set('faceplateUrl', faceplateUrl);
    }

    return giftCardItem.asImmutable();
};

const getFieldValue = (item, field, defaultValue = '') => (
    item && item.get ? item.get(field) || defaultValue : defaultValue);

/**
 * Determines recipient details based on purchase type (self or gift)
 * @param {Immutable.Map} item - Physical item data
 * @param {string} fullName - Full name from shipping address
 * @return {Object} Normalized recipient information
 */
const determineRecipientDetails = (item, fullName) => {
    const selfBuy = getFieldValue(item, 'selfBuy', false);

    
    if (selfBuy) {
        return {
            senderName: getFieldValue(item, 'yourName'),
            recipientName: getFieldValue(item, 'yourName'),
            recipientEmail: getFieldValue(item, 'yourEmail')
        };
    }

    
    const hasNotification = getFieldValue(item, 'hasNotification');
    const recipientEmail = getFieldValue(item, 'recipientEmail');
    const senderName = getFieldValue(item, 'senderName');

    return {
        senderName,
        recipientName: fullName,
        recipientEmail: (!hasNotification && !recipientEmail)
            ? getFieldValue(item, 'senderEmail')
            : recipientEmail
    };
};

const extractCertDetails = (item, faceplate, brandCode, fees, feeType, fullName, currencyCode, uyoPlasticPhotoSupported) => {
    
    const amount = Number(getFieldValue(item, 'cardValue', 0));
    const selfBuy = getFieldValue(item, 'selfBuy', false);

    
    const recipientDetails = determineRecipientDetails(item, fullName);

    let accessory = null;
    if (itemHasAccessory(item)) {
        accessory = accessorySerializer(getItemAccessory(item), currencyCode);
    }

    
    return {
        ...recipientDetails,
        deliveryDate: getFieldValue(item, 'deliveryDate', new Date().toISOString()),
        faceplateCode: getFieldValue(faceplate, 'code'),
        faceplateUrl: getFieldValue(faceplate, 'imageURL') || undefined,
        cartItemToken: getFieldValue(item, 'id'),
        message: getFieldValue(item, 'personalMessage'),
        plasticActivationFeeAmount: getFeeAmountByTypeAndCost(fees.toJS(), feeType, amount),
        quantity: getFieldValue(item, 'quantity', 1),
        faceValue: amount,
        brandCode,
        checkedEmail: Boolean(getFieldValue(item, 'recipientEmail')),
        isSharedViaLink: false,
        selfBuy,
        accessory,
        ...(uyoPlasticPhotoSupported && { uyoImageUrl: getFieldValue(item, 'UYOPImageURL') })
    };
};

export const shippingGroupSerializer = (item, brandCode, fees, feeType, currencyCode, uyoPlasticPhotoSupported) => {
    
    const address = item.get('shippingAddress');
    const fullName = getFieldValue(address, 'fullName');

    
    const faceplate = getFieldValue(item, 'faceplate', {});

    return {
        shippingAddress: filterAndMapKeys(address, shippingRecipientKeyMap).asMutable(),
        certs: [
            extractCertDetails(item, faceplate, brandCode, fees, feeType, fullName, currencyCode, uyoPlasticPhotoSupported)
        ],
        shippingAmount: parseFloat(getFieldValue(address, 'amount', 0)).toFixed(2),
        shippingMethodToken: getFieldValue(address, 'token')
    };
};
export const egiftCardsSerializer = (items, brandCode, fees = IList(), currencyCode, uyoPlasticPhotoSupported) => (
    items.map(egc => giftCardItemSerializer(egc, brandCode, fees, feeTypes.DIGITAL_ACTIVATION, currencyCode, uyoPlasticPhotoSupported))
);
export const physicalGiftCardsSerializer = (items, brandCode, fees = IList(), currencyCode, uyoPlasticPhotoSupported) => (
    items.map(item => shippingGroupSerializer(item, brandCode, fees, feeTypes.PLASTIC_ACTIVATION, currencyCode, uyoPlasticPhotoSupported)) 
);

export const metaDataSerializer = (metaData) => {
    const serializedMeta = filterAndMapKeys(metaData, metaDataKeyMap);

    
    
    serializedMeta.set('notifyOffers', false);
    serializedMeta.set('totalDiscountRewardAmount', 0);
    serializedMeta.set('promoCode', null);

    
    
    return serializedMeta.update('amount', val => (val ? val.toFixed(2) : undefined));
};

export const orderSerializer = (payment, orderMetaData, eGiftCards, physicalGiftCards, shippingPromoData,
    fees = IList(), customRewardCardRecipients = {}, cpfNumber = null, uyoPlasticPhotoSupported = false) => {
    const capiUriVersion = payment.get('CAPI_URI_VERSION', null);
    const paymentMethod = payment.get('paymentMethod');
    const brandCode = orderMetaData.get('brandCode');
    const currencyCode = orderMetaData.get('currencyCode');

    
    const paymentData = paymentSerializer(payment);
    const eGiftCardsData = egiftCardsSerializer(eGiftCards, brandCode, fees, currencyCode);
    const serializedMetaData = metaDataSerializer(orderMetaData);
    let shippingGroups = [];

    if (physicalGiftCards && physicalGiftCards.size > 0) {
        shippingGroups = physicalGiftCardsSerializer(physicalGiftCards, brandCode, fees, currencyCode, uyoPlasticPhotoSupported);
    }

    
    let orderData = serializedMetaData.merge({
        [paymentMethod]: paymentData,
        egiftCards: eGiftCardsData,
        shippingGroups,
        shippingPromoData,
        CAPI_URI_VERSION: capiUriVersion
    });

    if (cpfNumber) {
        orderData = orderData.set('localCustomerId', cpfNumber);
    }

    
    
    
    const serializedRecipients = customRewardCardRecipients && customRewardCardRecipients.map
        ? customRewardCardRecipients.map(
            recipient => recipient.mapKeys(key => (key === 'recipient_name' ? 'fullName' : key))
        )
        : customRewardCardRecipients;

    return orderData.set('customRewardCardRecipients', serializedRecipients);
};

export const userDataSerializer = (eGiftCards) => {
    const eGiftCard = eGiftCards.get(0);

    
    const fullName = (eGiftCard.get('selfBuy') ? eGiftCard.get('yourName') : eGiftCard.get('senderName')).trim();
    const [firstName, ...lastName] = (fullName).split(' ');

    const userData = {
        firstName,
        lastName: lastName.join(' ').trim() || 'LNU',
        emailAddress: eGiftCard.get('selfBuy') ? eGiftCard.get('yourEmail') : eGiftCard.get('senderEmail')
    };

    return IMap(userData);
};

export const userPhysicalDataSerializer = (physicalGiftCards) => {
    const physicalGiftCard = physicalGiftCards.get(0);

    
    const fullName = (physicalGiftCard.get('shippingAddress').get('fullName')).trim();
    const [firstName, ...lastName] = (fullName).split(' ');

    const userData = {
        firstName,
        lastName: lastName.join(' ').trim() || 'LNU',
        emailAddress: physicalGiftCard.get('selfBuy') ? physicalGiftCard.get('yourEmail') : physicalGiftCard.get('senderEmail')
    };

    return IMap(userData);
};

export const initiateConfigSerializer = (
    eGiftCards,
    orderMetaData,
    paymentPlusMetaData,
    riskifiedSessionId = null,
    fees = IList(),
    physicalGiftCards,
    shippingPromoData,
    promoRewards = IList(),
    cpfNumber = null,
    promoAccessoryData = null,
    uyoPlasticPhotoSupported = false
) => {
    const currencyCode = orderMetaData.get('currencyCode');
    const brandCode = orderMetaData.get('brandCode');
    let serializedMetaData = metaDataSerializer(orderMetaData);

    const config = {
        sessionIds: { RISKIFIED: riskifiedSessionId },
        ...paymentPlusMetaData
    };
    
    const useSeparateRewardCards = getPassingPromoDetailsEnabled();
    const allPromoRewards = promoRewards || IList();
    if (useSeparateRewardCards) {
        const promoRewardCards = allPromoRewards && allPromoRewards.size > 0
            ? allPromoRewards.map(reward => promoRewardToRewardCard(reward, brandCode)).toArray()
            : [];
        if (promoRewardCards.length > 0) {
            config.rewardCards = IList(promoRewardCards);
        }
    }
    if (promoAccessoryData && promoAccessoryData.size > 0) {
        config.promotionalAccessoryData = promoAccessoryData;
    }

    
    if (eGiftCards && eGiftCards.size > 0) {
        const normalizeInitiateConfigDigitalToken = (serializedCard, sourceCard) => {
            const existingToken = sourceCard && sourceCard.get ? sourceCard.get('cartItemToken') : null;
            return existingToken
                ? serializedCard.set('cartItemToken', existingToken)
                : serializedCard.delete('cartItemToken');
        };
        const eGiftCardsData = egiftCardsSerializer(
            eGiftCards,
            brandCode,
            fees,
            currencyCode
        ).map((card, index) => normalizeInitiateConfigDigitalToken(card, eGiftCards.get(index)));
        const userData = userDataSerializer(eGiftCards);
        config.user = userData;
        config.egiftCards = eGiftCardsData;
    }
    if (physicalGiftCards && physicalGiftCards.size > 0) {
        const normalizeInitiateConfigPhysicalToken = (group, sourceCard) => {
            const existingToken = sourceCard && sourceCard.get ? sourceCard.get('cartItemToken') : null;
            return fromJS(group).update('certs', certs => certs.map(cert => (
                existingToken
                    ? cert.set('cartItemToken', existingToken)
                    : cert.delete('cartItemToken')
            )));
        };
        const shippingGroups = physicalGiftCardsSerializer(
            physicalGiftCards,
            brandCode,
            fees,
            currencyCode,
            uyoPlasticPhotoSupported
        ).map((group, index) => normalizeInitiateConfigPhysicalToken(group, physicalGiftCards.get(index)));
        const userData = userPhysicalDataSerializer(physicalGiftCards);
        config.user = userData;
        config.shippingGroups = shippingGroups;
        if (shippingPromoData) {
            config.shippingPromoData = shippingPromoData;
        }
    }

    if (cpfNumber) {
        serializedMetaData = serializedMetaData.set('localCustomerId', cpfNumber);
    }

    return serializedMetaData.merge(config);
};

export const sanitizePaymentData = paymentData => (
    filterAndMapKeys(fromJS(paymentData), sanitizedPaymentKeyMap)
);

export const orderResponseSerializer = response => (
    filterAndMapKeys(fromJS(response), orderResponseKeyMap)
);
